import { ComponentType, LazyExoticComponent, lazy } from 'react';

// A new deploy renames the built chunk files. A browser that still holds the
// old index then asks for chunk names that no longer exist, so the dynamic
// import rejects and the lazy component throws while it renders. Without a
// retry this blanks the whole page.
//
// `lazyWithRetry` is a drop-in replacement for `React.lazy`. It keeps the same
// factory signature, so any `lazy(() => import('...'))` call site can switch to
// it without other changes.

const RELOAD_FLAG = 'bookstera:lazy-chunk-reloaded';

function getReloadFlag(factory: { toString(): string }) {
  // Each import factory is emitted with its chunk reference, which gives the
  // reload guard a stable, per-chunk key for a given build. A new deployment
  // produces a different factory/chunk reference, allowing one fresh retry.
  let hash = 5381;
  const source = factory.toString();
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 33) ^ source.charCodeAt(index);
  }
  return `${RELOAD_FLAG}:${hash >>> 0}`;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  const reloadFlag = getReloadFlag(factory);

  return lazy(async () => {
    try {
      // One in-memory retry absorbs a transient network hiccup.
      const component = await factory().catch(() => factory());
      sessionStorage.removeItem(reloadFlag);
      return component;
    } catch (error) {
      // Both attempts failed. One hard reload pulls the new index and chunk
      // map. A session flag stops a reload loop when the chunk stays broken;
      // the error then reaches the nearest ErrorBoundary for a fallback.
      if (sessionStorage.getItem(reloadFlag)) {
        throw error;
      }
      sessionStorage.setItem(reloadFlag, '1');
      window.location.reload();
      // Never resolve, so Suspense keeps the fallback until the reload replaces
      // the page.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
